export type CloudFile = {
  id: string;
  name?: string;
  size?: number;
  mimeType?: string;
  url?: string;
  raw?: any;
};

const isFile = (v: any): v is File =>
  typeof File !== "undefined" && v instanceof File;

const apiBase = () =>
  (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

function extractId(obj: any): string | undefined {
  return (
    obj?.id ||
    obj?._id ||
    obj?.fileId ||
    obj?.file_id ||
    obj?.file?.id ||
    obj?.data?.id ||
    obj?.data?._id ||
    undefined
  );
}

function extractUrl(obj: any): string | undefined {
  return (
    obj?.url ||
    obj?.presignedUrl ||
    obj?.presigned_url ||
    obj?.temporary_url ||
    obj?.download_url ||
    obj?.signedUrl ||
    obj?.data?.url ||
    obj?.data?.presignedUrl ||
    obj?.data?.presigned_url ||
    obj?.data?.temporary_url ||
    obj?.data?.download_url ||
    obj?.data?.signedUrl ||
    undefined
  );
}

function extractName(obj: any): string | undefined {
  return (
    obj?.name ||
    obj?.filename ||
    obj?.original_name ||
    obj?.stored_name ||
    obj?.data?.name ||
    obj?.data?.filename ||
    obj?.data?.original_name ||
    obj?.data?.stored_name ||
    undefined
  );
}

function extractMime(obj: any): string | undefined {
  return (
    obj?.mimeType ||
    obj?.mime_type ||
    obj?.mimetype ||
    obj?.contentType ||
    obj?.data?.mimeType ||
    obj?.data?.mime_type ||
    obj?.data?.mimetype ||
    obj?.data?.contentType ||
    undefined
  );
}

function extractSize(obj: any): number | undefined {
  return obj?.size ?? obj?.data?.size ?? undefined;
}

async function parseJson(res: Response) {
  try {
    return await res.json();
  } catch {
    return {};
  }
}

export async function uploadCloudFile(opts: {
  file: File;
  token: string | null;
  parentId?: string;
}): Promise<CloudFile> {
  const { file, token, parentId } = opts;

  const formData = new FormData();
  formData.append("file", file);
  if (parentId) formData.append("parent_id", parentId);

  const url = `${apiBase()}/work-drive/files/upload`;
  const res = await fetch(url, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  const json = await parseJson(res);
  if (!res.ok) {
    throw new Error(json?.message || json?.error || `Upload failed (${res.status})`);
  }

  const id = extractId(json);
  if (!id) {
    throw new Error("Upload succeeded but no file id returned");
  }

  return {
    id,
    name: extractName(json),
    size: extractSize(json),
    mimeType: extractMime(json),
    url: extractUrl(json),
    raw: json,
  };
}

/** Returns null when the file id does not exist (404) — never throws for missing files. */
export async function tryGetCloudFileById(
  fileId: string,
  token: string,
): Promise<CloudFile | null> {
  const url = `${apiBase()}/work-drive/files/${fileId}`;
  try {
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const json = await parseJson(res);
    if (!res.ok) return null;
    const id = extractId(json) || fileId;
    return {
      id,
      name: extractName(json),
      size: extractSize(json),
      mimeType: extractMime(json),
      url: extractUrl(json),
      raw: json,
    };
  } catch {
    return null;
  }
}

export async function getCloudFileById(fileId: string, token: string): Promise<CloudFile> {
  const file = await tryGetCloudFileById(fileId, token);
  if (!file) {
    throw new Error("File not found");
  }
  return file;
}

export async function uploadFormDataAndGetId(
  formData: FormData,
  token: string | null
): Promise<string> {
  const url = `${apiBase()}/work-drive/files/upload`;
  const res = await fetch(url, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  const json = await parseJson(res);
  if (!res.ok) {
    throw new Error(json?.message || json?.error || `Upload failed (${res.status})`);
  }

  const id = extractId(json);
  if (!id) {
    throw new Error("Upload succeeded but no file id returned");
  }
  return id;
}

export async function uploadAndGetId(
  file: File,
  token: string | null,
  parentId?: string
): Promise<string> {
  const fd = new FormData();
  fd.append("file", file);
  if (parentId) fd.append("parent_id", parentId);
  return uploadFormDataAndGetId(fd, token);
}

export async function resolveImageId(
  image: File | string | null | undefined,
  token: string | null,
  existingId?: string | null,
  parentId?: string
): Promise<string | undefined> {
  if (isFile(image)) {
    return uploadAndGetId(image, token, parentId);
  }
  if (typeof image === "string" && image.trim()) {
    return image.trim();
  }
  return existingId || undefined;
}

export async function resolveImageIds(
  images: Array<File | string> | File | string | null | undefined,
  token: string | null,
  existingIds?: string[] | null,
  parentId?: string
): Promise<string[] | undefined> {
  const arr: Array<File | string> =
    images == null ? [] : Array.isArray(images) ? images : [images];

  const out: string[] = [];
  for (const item of arr) {
    const id = await resolveImageId(item as any, token, undefined, parentId);
    if (id) out.push(id);
  }
  if (!out.length && Array.isArray(existingIds) && existingIds.length) return existingIds;
  return out.length ? Array.from(new Set(out)) : undefined;
}

export type PresignedMap = Record<string, string>;
const is24Hex = (s: unknown) => typeof s === "string" && /^[0-9a-f]{24}$/i.test(s);

async function postJson(url: string, token: string, body: any) {
  const res = await fetch(url, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json?.message || json?.error || `Request failed (${res.status})`);
  }
  return json;
}

export async function batchPresignUrls(fileIds: string[], token: string): Promise<PresignedMap> {
  if (!fileIds?.length) return {};
  const json = await postJson(`${apiBase()}/work-drive/files/presign`, token, { file_ids: fileIds });

  const map: PresignedMap = {};
  const absorb = (obj: any) => {
    if (!obj) return;
    if (Array.isArray(obj)) {
      obj.forEach((x) => {
        const id = extractId(x);
        const url = extractUrl(x) || x?.url;
        if (id && url) map[id] = url;
      });
      return;
    }
    if (typeof obj === "object") {
      for (const [k, v] of Object.entries(obj)) {
        if (typeof v === "string" && is24Hex(k)) map[k] = v;
        else if (v && typeof v === "object") {
          const id = extractId(v) || (is24Hex(k) ? k : undefined);
          const url = extractUrl(v);
          if (id && url) map[id] = url;
        }
      }
    }
  };

  absorb(json?.data);
  absorb(json?.files);
  absorb(json?.results);
  absorb(json);

  return map;
}

export function collectCloudIds<T extends Record<string, any>>(rows: T[], field: keyof T | string): string[] {
  const out: string[] = [];
  rows?.forEach((r) => {
    const v = (r as any)?.[field as any];
    if (is24Hex(v)) out.push(String(v));
  });
  return Array.from(new Set(out));
}

export async function uploadFilesAndGetIds(files: File[], token: string | null, parentId?: string): Promise<string[]> {
  if (!files?.length) return [];
  const ids = await Promise.all(files.map(f => uploadAndGetId(f, token, parentId)));
  return Array.from(new Set(ids));
}

export async function hardDeletePresignedFiles(fileIds: string[], token: string | null): Promise<void> {
  const ids = (fileIds || []).filter(Boolean);
  if (ids.length === 0) return;

  const auth = (token || "").trim();
  if (!auth) throw new Error("Missing auth token");

  const url = `${apiBase()}/work-drive/files/bulk/hard`;
  const res = await fetch(url, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${auth}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ file_ids: ids }),
  });

  const json = await parseJson(res);
  if (!res.ok) {
    throw new Error(json?.message || json?.error || `Hard delete failed (${res.status})`);
  }
}

export async function hardDeleteCloudFile(fileId: string, token: string | null): Promise<void> {
  if (!fileId) throw new Error("Missing fileId");

  const auth = (token || "").trim();
  if (!auth) throw new Error("Missing auth token");

  const url = `${apiBase()}/work-drive/files/${fileId}/hard`;
  const res = await fetch(url, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${auth}` },
  });

  const json = await parseJson(res);
  if (!res.ok) {
    throw new Error(json?.message || json?.error || `Hard delete failed (${res.status})`);
  }
}

export async function hardDeleteCloudFiles(fileIds: string[], token: string | null): Promise<void> {
  for (const id of fileIds || []) {
    await hardDeleteCloudFile(id, token);
  }
}

/** Convert a File/Blob to a data URL for API payloads (e.g. data:image/jpeg;base64,...). */
export function fileToBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Failed to read file as base64"));
    reader.readAsDataURL(file);
  });
}
